import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { CalendarEvent } from '../types/calendar';

/** Day bucket key used by the calendar grid and agenda list. */
export const dayKey = (date: Date) => format(date, 'yyyy-MM-dd');

/**
 * The instant an event stops occupying calendar days.
 *
 * An end that lands exactly on a local midnight (10 Mar 09:00 → 11 Mar 00:00)
 * means "until the end of the 10th", not "into the 11th", so it is pulled back
 * a millisecond before the last day is derived. An unparseable or backwards end
 * degrades to the start instant, i.e. a single-day event.
 */
export function effectiveEndMs(startMs: number, endTime: number | null | undefined): number {
  if (endTime == null) return startMs;
  const end = new Date(endTime);
  const endMs = end.getTime();
  if (isNaN(endMs) || endMs < startMs) return startMs;
  if (endMs > startMs && startOfDay(end).getTime() === endMs) return endMs - 1;
  return endMs;
}

/**
 * Index events by day, expanding multi-day events across every day they cover
 * and clipping them to `[rangeStart, rangeEnd]`.
 *
 * Building this index once per month replaces scanning the whole event list
 * again for each of the 42 cells in the grid. Clipping to the range also caps
 * the per-event work, so one event with a wild `end_time` cannot stall a render.
 *
 * Each event appears at most once per day. Events are returned in start order.
 */
export function buildEventsByDay(
  events: CalendarEvent[],
  rangeStart: Date,
  rangeEnd: Date,
): Map<string, CalendarEvent[]> {
  const byDay = new Map<string, CalendarEvent[]>();
  const rangeStartMs = startOfDay(rangeStart).getTime();
  const rangeEndMs = endOfDay(rangeEnd).getTime();
  if (rangeEndMs < rangeStartMs) return byDay;

  for (const event of events) {
    const start = new Date(event.start_time);
    if (isNaN(start.getTime())) continue;

    const endMs = effectiveEndMs(start.getTime(), event.end_time);

    // Skip events that do not overlap the range at all.
    if (start.getTime() > rangeEndMs || endMs < rangeStartMs) continue;

    const firstDay = start.getTime() < rangeStartMs ? startOfDay(rangeStart) : startOfDay(start);
    const lastDayMs = startOfDay(endMs > rangeEndMs ? rangeEnd : new Date(endMs)).getTime();

    for (let day = firstDay; day.getTime() <= lastDayMs; day = addDays(day, 1)) {
      const key = dayKey(day);
      const existing = byDay.get(key);
      if (existing) existing.push(event);
      else byDay.set(key, [event]);
    }
  }

  for (const dayEvents of byDay.values()) {
    dayEvents.sort((a, b) => a.start_time - b.start_time);
  }
  return byDay;
}
